import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RowActions } from "@/components/dashboard/RowActions";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import usePagination from "@/lib/usePagination";
import TablePagination from "@/components/dashboard/TablePagination";

const EMPTY = { name: "", industry: "", contact_email: "", manpower_count: 0 };

export default function Clients() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [industries, setIndustries] = useState([]);
  const { page, setPage, totalPages, pageItems, rangeStart, rangeEnd, total } = usePagination(rows, 20);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [f, setF] = useState(EMPTY);

  const load = async () => setRows((await api.get("/clients")).data);
  useEffect(() => {
    load();
    (async () => {
      try { setIndustries((await api.get("/industries", { params: { active: true } })).data); } catch {}
    })();
  }, []);

  const openCreate = () => { setEditingId(null); setF(EMPTY); setOpen(true); };
  const openEdit = (c) => {
    setEditingId(c.client_id);
    setF({ name: c.name, industry: c.industry, contact_email: c.contact_email, manpower_count: c.manpower_count });
    setOpen(true);
  };
  const save = async () => {
    if (!f.name || !f.industry || !f.contact_email) return toast.error("Name, industry and email are required");
    try {
      const payload = { ...f, manpower_count: Number(f.manpower_count) || 0 };
      if (editingId) await api.put(`/clients/${editingId}`, payload);
      else await api.post("/clients", payload);
      toast.success(editingId ? "Client updated" : "Client added");
      setOpen(false); setF(EMPTY); setEditingId(null); load();
    } catch (e) { toast.error("Failed"); }
  };
  const del = async (id) => {
    try { await api.delete(`/clients/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="space-y-8" data-testid="clients-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="label-eyebrow text-navy/50">Client network</div>
          <h1 className="serif-display text-4xl mt-2">Clients</h1>
        </div>
        {user.role === "admin" && <button className="btn-primary" onClick={openCreate} data-testid="new-client-btn"><Plus size={14}/> Add client</button>}
      </div>
      <div className="bg-white border border-navy/10">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Company</TableHead>
              <TableHead>Industry</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Manpower</TableHead>
              <TableHead>Onboarded</TableHead>
              {user.role === "admin" && <TableHead className="text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {pageItems.map((c) => (
              <TableRow key={c.client_id}>
                <TableCell className="serif-display text-lg">{c.name}</TableCell>
                <TableCell>{c.industry}</TableCell>
                <TableCell>{c.contact_email}</TableCell>
                <TableCell className="serif-display text-gold">{c.manpower_count}</TableCell>
                <TableCell className="text-navy/60 text-sm">{new Date(c.onboarded_at).toLocaleDateString()}</TableCell>
                {user.role === "admin" && (
                  <TableCell>
                    <RowActions
                      onEdit={() => openEdit(c)}
                      onDelete={() => del(c.client_id)}
                      editTestid={`edit-client-${c.client_id}`}
                      deleteTestid={`delete-client-${c.client_id}`}
                      itemLabel={c.name}
                    />
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          page={page} totalPages={totalPages} rangeStart={rangeStart} rangeEnd={rangeEnd} total={total}
          onPrev={() => setPage((p) => Math.max(1, p - 1))}
          onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
          testidPrefix="clients-table"
        />
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="serif-display text-2xl">{editingId ? "Edit client" : "Add client"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Company name</Label><Input value={f.name} onChange={e=>setF({...f, name: e.target.value})} data-testid="client-name"/></div>
            <div>
              <Label>Industry</Label>
              <Select value={f.industry} onValueChange={(v)=>setF({...f, industry: v})}>
                <SelectTrigger data-testid="client-industry"><SelectValue placeholder="Select industry"/></SelectTrigger>
                <SelectContent>
                  {industries.length === 0 && <div className="p-2 text-sm text-navy/60">No industries defined yet. Add one under Industries.</div>}
                  {industries.map(i => <SelectItem key={i.industry_id} value={i.name}>{i.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Contact email</Label><Input type="email" value={f.contact_email} onChange={e=>setF({...f, contact_email: e.target.value})}/></div>
            <div><Label>Manpower count</Label><Input type="number" value={f.manpower_count} onChange={e=>setF({...f, manpower_count: e.target.value})}/></div>
          </div>
          <DialogFooter>
            <button className="btn-outline" onClick={()=>setOpen(false)}>Cancel</button>
            <button className="btn-primary" onClick={save} data-testid="client-save-submit">{editingId ? "Save changes" : "Add client"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
